import { Component, Output, ViewChild, EventEmitter } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute, Params } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { DatePipe } from "@angular/common"
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { DateTime } from "luxon";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ValidationService } from "../service/validation.service";
import { AccountSelectData } from "../obj/AccountSelectData";
import { Account } from "../entity/Account";
import { Utility } from "../entity/Utility";
import { ExistingContract } from "../entity/ExistingContract";
import { EntityBase } from "../entity/EntityBase";
import { MatDatepicker, MatDatepickerInput } from "@angular/material/datepicker";
import { HeaderComponent } from "./incs/header.component";
import { NgxMaskDirective } from "ngx-mask";

export const MY_FORMATS = {
    parse: {
        dateInput: "MM/dd/yyyy",
    },
    display: {
        dateInput: "MM/dd/yyyy",
        monthYearLabel: "MMM yyyy",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM yyyy",
    },
};

@Component({
    selector: "kilowant-existing-contract-details",
    templateUrl: "../../html/main-existing-contract-details.html",
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    imports: [HeaderComponent, FormsModule, NgxMaskDirective, MatDatepickerInput, ReactiveFormsModule, MatDatepicker]
})
export class ExistingContractDetailsComponent extends AbstractBuyerComponent {
    @ViewChild("multiSelect") multiSelect: any;
    @Output() cancelled = new EventEmitter<boolean>();

    dropdownSettings: IDropdownSettings = {
        singleSelection: false,
        idField: "id",
        textField: "name",
        selectAllText: "Select All",
        unSelectAllText: "UnSelect All",
        itemsShowLimit: 1,
        allowSearchFilter: false,
        closeDropDownOnSelection: false
    };

    existingContractId: number = 0;
    globalStart: DateTime;
    globalEnd: DateTime;
    accounts: Account[] = [];
    accountsSelector: AccountSelectData[] = [];
    selectedAccountsData: any[] = [];

    existingContract: ExistingContract = new ExistingContract();
    termFrom = new UntypedFormControl();
    termTo = new UntypedFormControl();
    utility: Utility = null!;
    utilities: Utility[] = [];
    deleteExistingDocument: boolean = false;
    step: number = 1;
    fileToUpload: File | null = null;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        dialog: MatDialog,
        public validationService: ValidationService,
        private datepipe: DatePipe
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.analyticsService.trackEvent("Existing Contract Details Page", "User Opens Existing Contract Details Page", "OPEN_PAGE");
        super.ngOnInit();
    }

    pageStart(): void {
        this.route.params.forEach((params: Params) => {
            let id: string = params["id"];
            if (!(null == id)) {
                this.existingContractId = +id;
            }
        });
        this.globalStart = this.currentDate.minus({ years: 5 });
        this.globalEnd = this.currentDate.plus({ years: 5 });

        this.generalService.getAccounts(true)
            .then(accounts => {
                this.accounts = accounts;
                this.accountsSelector = this.generateAccountsSelect(this.accounts, (x: any) => { return x; });
                this.utilities = this._getUtilities(this.accounts);
                if (this.utilities.length == 1) {
                    this.utility = this.utilities[0];
                }
                this.reloadExistingContract();
                this.refreshView();
            }
            );
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    inactiveDatesFilter(d: DateTime | null): boolean {
        if (d == null || this.globalStart == null || this.globalEnd == null) {
            return false;
        }
        return (d > this.globalStart) && (d < this.globalEnd);
    }

    inactiveDatesFilterTermTo(d: DateTime | null): boolean {
        if (d == null) {
            return false;
        }
        let from;
        if (this.termFrom == null || this.termFrom.value == null) {
            from = this.globalStart;
        } else {
            from = this.termFrom.value;
        }
        return (d > from) && d < this.globalEnd;
    }

    sendForm(): void {
        this.clearErrors();
        if (this.existingContract.accounts.length == 0) {
            this.addError("selectedAccounts");
        }
        if (
            this.existingContract.supplierName == null || this.existingContract.supplierName.length == 0
        ) {
            this.addError("currentSupplierName");
        }
        if (
            this.existingContract.price == null || this.existingContract.price == 0
        ) {
            this.addError("currentSupplierPrice");
        }
        if (this.termFrom.value == null || this.termFrom.value.length == 0) {
            this.addError("termFrom");
        }
        if (this.termTo.value == null || this.termTo.value.length == 0) {
            this.addError("termTo");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to register.");
            return;
        }

        if (this.termFrom.value >= this.termTo.value) {
            this.addError("termFrom");
            this.addError("termTo");
            this.setErrorText("The ending price plan date shall be ahead of the starting price plan date.");
            return;
        }

        if (!this.validationService.validate("ExistingContract", "supplierName", this.existingContract.supplierName, this.errorY)) {
            return;
        }

        this.existingContract.termFrom = this.termFrom.value.toJSDate();
        this.existingContract.termTo = this.termTo.value.toJSDate();
        if (this.existingContractId > 0) {
            this.generalService.updateExistingContract(this.existingContract, this.fileToUpload, this.deleteExistingDocument)
                .subscribe((res: any) => {
                    if (res) {
                        this.router.navigate(["/user/home"], { queryParams: { confirm: "update-existing-priceplan", contractId: this.existingContractId } });
                        return;
                    }
                },
                    err => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    });
        } else {
            this.generalService.addExistingContract(this.existingContract, this.fileToUpload)
                .subscribe((res: any) => {
                    if (res) {
                        this.router.navigate(["/user/home"], { queryParams: { confirm: "add-existing-priceplan", contractId: res } });
                    }
                },
                    err => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    });
        }
    }

    gotoStep(num: number): void {
        this.step = num;
        this.clearErrors();
    }

    reloadExistingContract(): void {
        if (this.existingContractId > 0) {
            this.generalService.getExistingContract(this.existingContractId)
                .then(e => {
                    this.existingContract = e;
                    if (this.existingContract.accounts.length > 0 && this.existingContract.accounts[0].utility != null) {
                        this.utility = <Utility>this.existingContract.accounts[0].utility;
                    }
                    this.termFrom = new UntypedFormControl(DateTime.fromJSDate(this.existingContract.termFrom));
                    this.termTo = new UntypedFormControl(DateTime.fromJSDate(this.existingContract.termTo));
                    var accountsIds = [];
                    for (var i = 0; i < this.existingContract.accounts.length; i++) {
                        accountsIds.push(this.existingContract.accounts[i].id);
                    }
                    this.automaticallySelectAccounts(accountsIds);
                });
        } else {
            this.existingContract.accounts = [];
            this.preloadAccounts();
        }
    }

    preloadAccounts(): void {
        if (this.accounts.length == 1) {
            this.automaticallySelectAccounts([this.accounts[0].id]);
            this.existingContract.accounts.push(this.accounts[0]);
            this.finishStep1();
            return;
        }
        var account = this.generalService.getStoredAccount();
        if (account != null) {
            this.automaticallySelectAccounts([account.id]);
            this.existingContract.accounts.push(account);
        }
    }

    getAccountElement(accountId: number): any {
        for (var i = 0; i < this.accountsSelector.length; i++) {
            if (this.accountsSelector[i].id == accountId) {
                return this.accountsSelector[i];
            }
        }
        return null;
    }

    onItemSelect(item: any, ifAdd: boolean): void {
        if (item == null || item.id == null) {
            return;
        }
        var a = <Account>EntityBase.getValueFromList(this.accounts, item.id);
        if (!ifAdd) {
            this.addRemoveAccount(a, ifAdd);
        }
        var ident = "";
        for (var i = 0; i < this.multiSelect._data.length; i++) {
            if (this.multiSelect.isSelected(this.multiSelect._data[i])) {
                ident = this.accountsSelector[i].groupIdent;
                break;
            }
        }
        for (var i = 0; i < this.multiSelect._data.length; i++) {
            if (ident != "" && this.accountsSelector[i].groupIdent != ident) {
                this.multiSelect._data[i].isDisabled = true;
            } else {
                this.multiSelect._data[i].isDisabled = false;
            }
        }
        this.addRemoveAccount(a, ifAdd);
    }

    addRemoveAccount(xAccount: Account, adding: boolean): void {
        if (adding && !this.accountIncludes(xAccount)) {
            this.existingContract.accounts.push(xAccount);
        }
        if (!adding) {
            EntityBase.removeValueFromList(this.existingContract.accounts, xAccount.id);
        }
        this.resetError("selectedAccounts");
        this.refreshView();
    }

    accountIncludes(xAccount: Account): boolean {
        return EntityBase.includes(this.existingContract.accounts, xAccount);
    }

    ifItemSelected(id: number): boolean {
        if (this.existingContract == null || this.existingContract.accounts == null) {
            return false;
        }
        for (let i = 0; i < this.existingContract.accounts.length; i++) {
            if (this.existingContract.accounts[i].id == id) {
                return true;
            }
        }
        return false;
    }

    ifItemSelectable(id: number): boolean {
        var a;
        for (var i = 0; i < this.accounts.length; i++) {
            if (this.accounts[i].id == id) {
                a = this.accounts[i];
                break;
            }
        }
        if (a != undefined) {
            if (this.existingContract == null || this.existingContract.accounts == null) {
                return false;
            }
            if (this.existingContract.accounts.length == 0) {
                return true;
            }
            return (this.existingContract.accounts[0].utility?.id == a.utility?.id) && (this.existingContract.accounts[0].loadZone?.id == a.loadZone?.id);
        }
        return false;
    }

    selectItem(id: number): void {
        var a;
        for (var i = 0; i < this.accounts.length; i++) {
            if (this.accounts[i].id == id) {
                a = this.accounts[i];
                break;
            }
        }
        if (a != undefined) {
            if (this.ifItemSelected(a.id)) {
                this.addRemoveAccount(a, false);
            } else {
                this.addRemoveAccount(a, true);
            }
        }
    }

    clearAccounts(): void {
        this.existingContract.accounts = [];
        this.resetError("utility");
    }

    finishStep1(): void {
        this.clearErrors();
        if (this.existingContract.accounts.length == 0) {
            this.addError("selectedAccounts");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above.");
            return;
        }
        this.clearErrors();
        this.step = 2;
    }

    finishStep2(): void {
        this.clearErrors();
        if (this.existingContract.supplierName == null || this.existingContract.supplierName.length == 0) {
            this.addError("currentSupplierName");
        }
        if (this.existingContract.price == null || this.existingContract.price == 0) {
            this.addError("currentSupplierPrice");
        }
        if (this.termFrom.value == null) {
            this.addError("termFrom");
        }
        if (this.termTo.value == null) {
            this.addError("termTo");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above.");
            return;
        }
        if (this.termFrom.value >= this.termTo.value) {
            this.addError("termFrom");
            this.addError("termTo");
            this.setErrorText("The ending price plan date shall be ahead of the starting price plan date.");
            return;
        }
        if (!this.validationService.validate("ExistingContract", "supplierName", this.existingContract.supplierName, this.errorY)) {
            this.addError("currentSupplierName");
            return;
        }
        this.clearErrors();
        this.step = 3;

    }

    cancel(): void {
        this.cancelled.next(true);
        this.utility = null!;
        this.router.navigate([(this.previousUrl) ? this.previousUrl : "/user/home"]);
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
    }

    deleteFile(ifExisting: boolean) {
        if (!ifExisting) {
            this.fileToUpload = null!;
        } else {
            this.existingContract.contractDocument = null!;
            this.deleteExistingDocument = true;
        }
    }

    downloadFile(ifExisting: boolean) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else {
            this.generalService.sendFileFromServerToClient(this.existingContract.contractDocument.key);
        }
    }

    private automaticallySelectAccounts(selectedAccountsIdsArr: number[]) {
        this.selectedAccountsData = [];
        for (var i = 0; i < selectedAccountsIdsArr.length; i++) {
            for (var x = 0; x < this.accountsSelector.length; x++) {
                if (this.accountsSelector[x].id == selectedAccountsIdsArr[i]) {
                    this.selectedAccountsData.push(this.accountsSelector[x]);
                }
            }
        }
        this.refreshView();
    }

    boundInactiveDatesFilter = this.inactiveDatesFilter.bind(this);
    boundInactiveDatesFilterTermTo = this.inactiveDatesFilterTermTo.bind(this);

}
